import { useMemo } from 'react';

import { percentageFormatterNoSign } from '@/lib/formatting';

import { TextSkeleton } from '@/components/Skeleton';
import { TokenPriceChart } from '@/components/TokenPriceChart';

import { WRAPPED_NATIVE_ADDRESSES } from '@/constants/addresses';

import { useFiatDisplay } from '@/hooks/useFiatDisplay';
import { useTokenInfo } from '@/hooks/useTokenInfo';
import { useUSDPrices } from '@/hooks/useUSDPrices';

import { fontStyles } from '@/styles/typography';

import type { ChartPadding } from '@/ui/Charts/types';

import type { TokenResult } from '@/utils/0x/token-registry.types';
import { classNames } from '@/utils/classnames';
import { isTokenResultNativeAsset } from '@/utils/multichain';

const PREVIEW_CHART_PADDING: ChartPadding = { top: 0, right: 0, bottom: 38, left: 0 };

export const Chart = ({ token }: { token?: TokenResult }) => {
  const { formatFiat } = useFiatDisplay();
  const { address, chainId } = token ?? {};
  const { data: tokenInfo, isLoading: isTokenInfoLoading } = useTokenInfo(address, chainId);

  const usdPriceInputs = useMemo(() => {
    if (!token) return undefined;

    const { address: tokenAddress, chainId: tokenChainId, issuer } = token;
    const priceAddress = isTokenResultNativeAsset(token)
      ? WRAPPED_NATIVE_ADDRESSES[tokenChainId]
      : tokenAddress;

    return [{ address: priceAddress, networkId: tokenChainId, issuer }];
  }, [token]);

  const { data: usdPrices, isLoading: isUsdPricesLoading } = useUSDPrices(usdPriceInputs);

  const priceChangePercentage = tokenInfo?.info?.market_data?.price_change_percentage_24h;
  const hasPriceChange = priceChangePercentage != null && priceChangePercentage !== 0;

  const isPositivePriceChange = hasPriceChange && priceChangePercentage > 0;
  const firstUsdPrice = usdPrices?.[0];
  const formattedPrice = firstUsdPrice ? formatFiat(firstUsdPrice.priceUsd) : undefined;
  const isSummaryLoading = token == null || isTokenInfoLoading || isUsdPricesLoading;

  return (
    <div className="relative mt-4">
      <TokenPriceChart token={token} padding={PREVIEW_CHART_PADDING} height={138} />
      {isSummaryLoading ? (
        <div className="flex absolute bottom-0 w-full justify-between px-4 pb-2">
          <TextSkeleton width={90} height={22} />
          <TextSkeleton width={70} height={22} />
        </div>
      ) : (
        <div
          className={classNames(
            'flex absolute bottom-0 w-full px-4 pb-2',
            hasPriceChange ? 'justify-between' : 'justify-end',
          )}
        >
          {hasPriceChange && (
            <p
              className={fontStyles({
                variant: 'BODY_MD',
                class: isPositivePriceChange
                  ? 'text-primitive-green-900'
                  : 'text-primitive-red-900',
              })}
            >
              {isPositivePriceChange ? '↑' : '↓'}
              {percentageFormatterNoSign.format(Math.abs(priceChangePercentage) / 100)}
              <span className="pl-1 text-primitive-gray-700">(24h)</span>
            </p>
          )}
          {formattedPrice && <p className={fontStyles({ variant: 'BODY_MD' })}>{formattedPrice}</p>}
        </div>
      )}
    </div>
  );
};
